<!-- Notifications toggle -->
<button mat-icon-button (click)="openPanel()" #notificationsOrigin>
    <ng-container *ngIf="unreadCount + syncQueueCount > 0">
        <span
            class="absolute top-0 right-0 left-0 flex items-center justify-center h-3"
        >
            <span
                class="flex items-center justify-center shrink-0 min-w-4 h-4 px-1 ml-4 mt-2.5 rounded-full bg-teal-600 text-indigo-50 text-xs font-medium"
            >
                {{ unreadCount + syncQueueCount }}
            </span>
        </span>
    </ng-container>
    <mat-icon
        [ngClass]="{ 'text-white': isWhiteIcon }"
        [svgIcon]="'heroicons_outline:bell'"
    ></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
    <div
        class="fixed inset-0 sm:static sm:inset-auto flex flex-col sm:min-w-90 sm:w-90 sm:rounded-2xl overflow-hidden shadow-lg"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center py-4 pr-4 pl-6 bg-primary text-on-primary"
        >
            <div class="text-lg font-medium leading-10">Notifications</div>
            <div class="ml-auto flex items-center gap-2">
                <!-- Sync Now button (visible when pending changes exist) -->
                <button
                    *ngIf="syncQueueCount > 0"
                    mat-icon-button
                    [matTooltip]="'Sync pending changes'"
                    [disabled]="isSyncing"
                    (click)="triggerManualSync()"
                >
                    <mat-icon
                        class="icon-size-5 text-current"
                        [svgIcon]="
                            isSyncing
                                ? 'heroicons_solid:arrow-path'
                                : 'heroicons_solid:cloud-arrow-up'
                        "
                        [class.animate-spin]="isSyncing"
                    ></mat-icon>
                </button>
                <!-- Mark all as read -->
                <button
                    mat-icon-button
                    [matTooltip]="'Mark all as read'"
                    [disabled]="unreadCount === 0"
                    (click)="markAllAsRead()"
                >
                    <mat-icon
                        class="icon-size-5 text-current"
                        [svgIcon]="'heroicons_solid:envelope-open'"
                    ></mat-icon>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div
            class="relative flex flex-col flex-auto sm:max-h-120 divide-y overflow-y-auto bg-card"
        >
            <!-- Sync Queue Notifications -->
            <ng-container
                *ngFor="let syncItem of syncNotifications; trackBy: trackByFn"
            >
                <div
                    class="flex flex-col group hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                >
                    <div class="flex cursor-default">
                        <div class="flex flex-auto py-5 pl-6">
                            <!-- Title, description & time -->
                            <div class="flex flex-col flex-auto">
                                <div class="font-semibold line-clamp-1">
                                    Pending Sync:
                                    {{ syncItem.wellName || syncItem.cdftId }}
                                </div>
                                <div class="line-clamp-2 text-sm">
                                    {{ syncItem.message }}
                                </div>
                                <div
                                    class="mt-2 text-xs leading-none text-gray-800"
                                >
                                    {{
                                        syncItem.timestamp
                                            | date: "MMM dd, h:mm a"
                                    }}
                                </div>
                                <div
                                    *ngIf="syncItem.error"
                                    class="mt-1 text-xs text-red-600"
                                >
                                    Error: {{ syncItem.error }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ng-container>

            <!-- Notifications -->
            <ng-container
                *ngFor="let notification of notifications; trackBy: trackByFn"
            >
                <div
                    class="flex group hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5 cursor-pointer"
                    [ngClass]="{ unread: !notification.isRead }"
                    (click)="markAsRead(notification)"
                    [attr.title]="!notification.isRead ? 'Mark as read' : null"
                >
                    <!-- Notification without a link -->
                    <ng-container *ngIf="!notification.link">
                        <div class="flex flex-auto py-5 pl-6">
                            <ng-container
                                *ngTemplateOutlet="notificationContent"
                            ></ng-container>
                        </div>
                    </ng-container>

                    <!-- Actions -->
                    <div class="relative flex flex-col my-5 mr-6 ml-2">
                        <!-- Indicator -->
                        <button
                            class="w-6 h-6 min-h-6"
                            mat-icon-button
                            [disabled]="notification.isRead"
                        >
                            <span
                                class="bg-gray-100 w-2 h-2 rounded-full"
                                [ngClass]="{
                                    'bg-primary': !notification.isRead,
                                }"
                            ></span>
                        </button>
                        <!-- Remove -->
                        <button
                            class="w-6 h-6 min-h-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="delete(notification)"
                            [matTooltip]="'Remove'"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:x-mark'"
                            ></mat-icon>
                        </button>
                    </div>
                </div>

                <!-- Notification content template -->
                <ng-template #notificationContent>
                    <!-- Icon -->
                    <ng-container
                        *ngIf="notification.icon && !notification.image"
                    >
                        <div
                            class="flex shrink-0 items-center justify-center w-8 h-8 mr-4 rounded-full bg-gray-100 dark:bg-gray-700"
                        >
                            <mat-icon
                                class="icon-size-5"
                                [svgIcon]="notification.icon"
                            >
                            </mat-icon>
                        </div>
                    </ng-container>
                    <!-- Title, description & time -->
                    <div class="flex flex-col flex-auto">
                        <ng-container *ngIf="notification.notificationType">
                            <div class="font-semibold line-clamp-1">
                                {{ notification.notificationType }}
                            </div>
                        </ng-container>
                        <ng-container *ngIf="notification.message">
                            <div class="line-clamp-2">
                                {{ notification.message }}
                            </div>
                        </ng-container>
                        <div class="mt-2 text-sm leading-none text-gray-800">
                            {{ notification.date | date: "MMM dd, h:mm a" }}
                        </div>
                    </div>
                </ng-template>
            </ng-container>

            <!-- No notifications -->
            <ng-container
                *ngIf="
                    (!notifications || !notifications.length) &&
                    (!syncNotifications || !syncNotifications.length)
                "
            >
                <div
                    class="flex flex-col flex-auto items-center justify-center sm:justify-start py-12 px-8"
                >
                    <div
                        class="flex flex-0 items-center justify-center w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-600"
                    >
                        <mat-icon
                            class="text-primary-700 dark:text-primary-50"
                            [svgIcon]="'heroicons_outline:bell'"
                        ></mat-icon>
                    </div>
                    <div class="mt-5 text-2xl font-semibold tracking-tight">
                        No notifications
                    </div>
                    <div
                        class="w-full max-w-60 mt-1 text-md text-center text-gray-800"
                    >
                        When you have notifications, they will appear here.
                    </div>
                </div>
            </ng-container>
        </div>
    </div>
</ng-template>
